"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Loader2, Star, User, X } from "lucide-react";
import { DockSheet } from "@/components/ui/dock-sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DIALOG_CARD_CONTENT_BASE,
  DIALOG_CARD_OVERLAY_CLASS,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/hooks/use-mobile";
import { useProfileReviews } from "@/hooks/use-profile-reviews";
import { ReviewCard } from "@/components/reviews/review-card";
import { StarRating } from "@/components/reviews/star-rating";
import { cn } from "@/lib/utils";
import type { ProfileReviewRole, ReceivedReviewRole } from "@/lib/reviews/schema";

// Role filter tabs. Presentational — the active role is owned by the dialog so
// the tabs can live in the header (flush with the title) instead of the
// scroll area. See ProfileReviewsDialog.
export function ReviewRoleTabs({
  roles,
  activeRole,
  onChange,
  dark = false,
  className,
}: {
  roles: ProfileReviewRole[];
  activeRole: ProfileReviewRole;
  onChange: (role: ProfileReviewRole) => void;
  dark?: boolean;
  className?: string;
}) {
  const t = useTranslations("reviews");
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {roles.map((role) => {
        const selected = activeRole === role;
        return (
          <button
            key={role}
            type="button"
            onClick={() => onChange(role)}
            className={cn(
              "rounded-full px-4 py-2 text-sm font-semibold transition-colors",
              selected
                ? dark
                  ? "bg-white text-neutral-900"
                  : "bg-foreground text-background"
                : dark
                  ? "bg-neutral-800 text-white"
                  : "bg-muted text-foreground",
            )}
          >
            {t(`titleAs.${role}`)}
          </button>
        );
      })}
    </div>
  );
}

type ProfileReviewsPanelProps = {
  profileId: string;
  role: ProfileReviewRole;
  enabled: boolean;
  dark?: boolean;
};

export function ProfileReviewsPanel({
  profileId,
  role,
  enabled,
  dark = false,
}: ProfileReviewsPanelProps) {
  const t = useTranslations("reviews");
  const effectiveRole = role;
  const isGivenTab = effectiveRole === "given";
  const { state, loadingMore, fetchNextPage } = useProfileReviews(
    enabled ? profileId : null,
    effectiveRole,
    enabled,
  );

  const stats = state.kind === "success" ? state.stats : null;
  const reviews = state.kind === "success" ? state.reviews : [];
  const hasMore = state.kind === "success" ? state.hasMore : false;
  const totalCount =
    state.kind === "success"
      ? state.totalCount
      : effectiveRole === "seller"
        ? stats?.as_seller_count ?? 0
        : effectiveRole === "buyer"
          ? stats?.as_buyer_count ?? 0
          : 0;

  const topBadges = useMemo(() => {
    if (effectiveRole === "given") return [];
    const counts =
      effectiveRole === "seller"
        ? stats?.as_seller_badge_counts ?? {}
        : stats?.as_buyer_badge_counts ?? {};
    return Object.entries(counts)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .slice(0, 5);
  }, [effectiveRole, stats]);

  // "Given" reviews are scores the profile handed out, not its own reputation —
  // an average/star summary there would be misleading, so only received tabs show it.
  const avgScore = isGivenTab
    ? null
    : effectiveRole === "seller"
      ? stats?.as_seller_avg ?? null
      : stats?.as_buyer_avg ?? null;
  const reviewCount = totalCount;

  return (
    <div className="space-y-4">
      {state.kind === "loading" ? (
        <div className="flex justify-center py-12">
          <Loader2 className={cn("h-8 w-8 animate-spin", dark ? "text-white" : "text-muted-foreground")} />
        </div>
      ) : state.kind === "error" ? (
        <p className={cn("py-8 text-center text-sm", dark ? "text-neutral-400" : "text-muted-foreground")}>
          {t("profileLoadError")}
        </p>
      ) : (
        <>
          {isGivenTab ? (
            reviewCount > 0 ? (
              <p
                className={cn(
                  "py-4 text-center text-sm",
                  dark ? "text-neutral-400" : "text-muted-foreground",
                )}
              >
                {t("profileCount", { count: reviewCount })}
              </p>
            ) : null
          ) : (
            <div className="flex flex-col items-center gap-1 py-4 text-center">
              <p className={cn("text-4xl font-bold tabular-nums", dark && "text-white")}>
                {typeof avgScore === "number" ? avgScore.toFixed(1) : "—"}
              </p>
              <StarRating value={avgScore ?? 0} readOnly size={22} />
              {reviewCount > 0 ? (
                <p className={cn("text-sm", dark ? "text-neutral-400" : "text-muted-foreground")}>
                  {t("profileCount", { count: reviewCount })}
                </p>
              ) : null}
            </div>
          )}

          {topBadges.length > 0 ? (
            <div className="space-y-2">
              <h3 className={cn("text-base font-semibold", dark && "text-white")}>{t("topBadges")}</h3>
              <div className="flex flex-wrap gap-2">
                {topBadges.map(([code, count]) => (
                  <span
                    key={code}
                    className={
                      dark
                        ? "inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-sm font-semibold text-secondary-foreground"
                        : "inline-flex items-center gap-1 rounded-full border bg-muted/50 px-3 py-1 text-sm font-semibold"
                    }
                  >
                    {t(`badges.${code}`)}
                    <span className={dark ? "text-neutral-400" : "text-muted-foreground"}>
                      ({count as number})
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {reviews.length === 0 ? (
            <p className={cn("py-8 text-center text-sm", dark ? "text-neutral-400" : "text-muted-foreground")}>
              {t("profileEmpty")}
            </p>
          ) : (
            <div key={effectiveRole} className="space-y-3">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} dark={dark} />
              ))}
            </div>
          )}

          {hasMore ? (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant={dark ? "secondary" : "outline"}
                className={dark ? "bg-neutral-800 text-white hover:bg-neutral-700" : undefined}
                disabled={loadingMore}
                onClick={fetchNextPage}
              >
                {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type ProfileReviewsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileId: string;
  initialRole?: ReceivedReviewRole;
  showGiven?: boolean;
};

export function ProfileReviewsDialog({
  open,
  onOpenChange,
  profileId,
  initialRole = "seller",
  showGiven = false,
}: ProfileReviewsDialogProps) {
  const isMobile = useMobile();
  const t = useTranslations("reviews");
  const tCommon = useTranslations("common");
  const title = t("profileTitle");

  const [activeRole, setActiveRole] = useState<ProfileReviewRole>(initialRole);
  // Clamp away the given tab if it's been hidden while it was active.
  const effectiveRole: ProfileReviewRole =
    activeRole === "given" && !showGiven ? "seller" : activeRole;
  const tabRoles: ProfileReviewRole[] = showGiven
    ? ["seller", "buyer", "given"]
    : ["seller", "buyer"];

  useEffect(() => {
    if (open) setActiveRole(initialRole);
  }, [open, initialRole, profileId]);

  const tabs = (
    <ReviewRoleTabs
      roles={tabRoles}
      activeRole={effectiveRole}
      onChange={setActiveRole}
      dark
    />
  );

  const panel = (
    <ProfileReviewsPanel
      profileId={profileId}
      role={effectiveRole}
      enabled={open}
      dark
    />
  );

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        belowTitle={tabs}
        zIndex={10050}
        contentClassName="pt-1 overflow-x-hidden"
      >
        <div className="flex flex-col gap-4">{panel}</div>
      </DockSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        noTransform
        overlayClassName={DIALOG_CARD_OVERLAY_CLASS}
        className={cn(DIALOG_CARD_CONTENT_BASE, "max-w-lg p-0 gap-0 overflow-hidden")}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex max-h-[min(85dvh,720px)] flex-col text-white">
          <div className="relative shrink-0 border-b border-white/10 px-4 pb-3 pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 transition-colors hover:bg-white/15"
              aria-label={tCommon("close")}
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <h2 className="pr-10 text-center text-lg font-semibold">{title}</h2>
            <div className="mt-3">{tabs}</div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{panel}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProfileReviewsMenuIcon() {
  return <Star className="mr-2 h-4 w-4" />;
}

export function ReviewCounterpartyAvatar({
  url,
  name,
  className,
}: {
  url?: string | null;
  name: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted ring-1 ring-border/60",
        className,
      )}
    >
      {url ? (
        <Image src={url} alt={name} fill className="object-cover" sizes="40px" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <User className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

export type ReviewShellProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
};

export function ReviewResponsiveShell({
  open,
  onOpenChange,
  title,
  children,
  footer,
}: ReviewShellProps) {
  const isMobile = useMobile();
  const tCommon = useTranslations("common");

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        footer={footer}
        zIndex={10050}
        contentClassName="pt-1 overflow-x-hidden"
      >
        <div className="flex flex-col gap-4">{children}</div>
      </DockSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        noTransform
        overlayClassName={DIALOG_CARD_OVERLAY_CLASS}
        className={cn(
          DIALOG_CARD_CONTENT_BASE,
          "flex h-[min(90dvh,720px)] max-h-[min(90dvh,720px)] w-full max-w-md flex-col p-0 gap-0 overflow-hidden",
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col text-white">
          <div className="relative shrink-0 border-b border-white/10 px-4 pb-3 pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 transition-colors hover:bg-white/15"
              aria-label={tCommon("close")}
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <h2 className="pr-10 text-center text-lg font-semibold leading-tight">{title}</h2>
          </div>
          {footer ? (
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <div
                className="absolute inset-0 overflow-y-auto overflow-x-hidden px-4 py-4 scrollbar-none [&::-webkit-scrollbar]:hidden"
                style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}
              >
                <div className="flex flex-col gap-4">{children}</div>
              </div>
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20 bg-linear-to-t from-neutral-900 to-transparent"
                aria-hidden
              />
              <div
                className="absolute inset-x-0 bottom-0 z-20 px-4 pt-4"
                style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {footer}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
              {children}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
